import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { carouselSlides } from '@/mocks/news';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % carouselSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const slide = carouselSlides[index];

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  return (
    <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden bg-primary-950">
      {carouselSlides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            title={`${item.league} ${item.title}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/50 to-primary-950/75" />

      <div className="relative z-10 w-full h-full px-4 md:px-6 flex items-center justify-center">
        <div className="w-full max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs md:text-sm tracking-wide">
            <i className="ri-record-circle-line w-4 h-4 flex items-center justify-center text-accent-400" />
            今日大赛聚集地 · 实时更新
          </span>
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-background-50 tracking-wide">
            51每日大赛
          </h1>
          <p className="mt-4 text-sm md:text-lg text-background-100/90 leading-relaxed max-w-2xl mx-auto">
            聚合最新赛事资讯、往期黑5数据统计与深度分析，帮助每一位体育爱好者快速了解今日大赛动态。
          </p>

          <form onSubmit={submitSearch} className="mt-7 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 bg-background-50/95 backdrop-blur rounded-full p-1.5 md:p-2">
              <i className="ri-search-line ml-3 text-foreground-500 w-5 h-5 flex items-center justify-center" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索赛事名称、球队、黑5数据关键词"
                className="flex-1 bg-transparent px-1 py-2 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 outline-none min-w-0"
              />
              <button
                type="submit"
                className="shrink-0 px-4 md:px-6 py-2 md:py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                搜索
              </button>
            </div>
            <div className="mt-3 flex items-center justify-center gap-3 flex-wrap text-xs text-background-100/80">
              <span>热门搜索：</span>
              <Link to="/search?q=黑5" className="hover:text-accent-400 transition-colors">黑5数据</Link>
              <Link to="/search?q=英超" className="hover:text-accent-400 transition-colors">英超</Link>
              <Link to="/search?q=NBA" className="hover:text-accent-400 transition-colors">NBA</Link>
              <Link to="/search?q=LPL" className="hover:text-accent-400 transition-colors">LPL</Link>
              <Link to="/search?q=温网" className="hover:text-accent-400 transition-colors">温网</Link>
            </div>
          </form>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 px-4 md:px-6 pb-5">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <Link
            to={`/detail/${slide.eventId}`}
            className="group max-w-xl bg-primary-950/45 backdrop-blur-sm rounded-lg p-4 hover:bg-primary-950/65 transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${
                slide.status === '进行中' ? 'bg-accent-500 text-background-50' : 'bg-background-50/90 text-foreground-800'
              }`}>
                {slide.status}
              </span>
              <span className="text-xs text-background-100/80">{slide.league}</span>
            </div>
            <h2 className="font-heading text-base md:text-lg font-semibold text-background-50 leading-snug group-hover:text-accent-300 transition-colors">
              {slide.title}
            </h2>
            <p className="mt-1 text-xs md:text-sm text-background-100/80 line-clamp-1 md:line-clamp-2">
              {slide.subtitle}
            </p>
          </Link>

          <div className="flex items-center gap-2">
            {carouselSlides.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`查看第 ${i + 1} 条焦点内容`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}